import ActiveToggleButton from "@components/ActiveToggleButton";
import { BaseTable } from "@components/BaseTable";
import CustomButton from "@components/common/Button";
import { DropdownService, PrintFormatService } from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import { tableName } from "@/utils/constant/tableName";
import navigationRoutes from "@/utils/navigationRoutes";
import { PrintFormat } from "@/utils/types/printFormat";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const PrintFormatListPage = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["printFormats", page, pageSize],
    queryFn: () => PrintFormatService.getAllPrintFormats({ page, pageSize }),
  });

  const handleDelete = async (id: string) => {
    const response = await PrintFormatService.deletePrintFormat(id);
    if (response) {
      refetch();
    }
  };
  const { data: schemas } = useQuery({
    queryKey: ["schemas"],
    queryFn: () => DropdownService.getDropdownData("schema"),
  });

  // Helper functions for displaying data
  const getSchemaMasterById = (id: string) => {
    return schemas?.find(
      (schema: { value: string; label: string }) => schema.value === id
    );
  };
  const gridData = data?.results ?? [];
  const total = data?.count ?? 0;

  const columns: ProColumns<PrintFormat>[] = [
    {
      title: "Print Format Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name!.localeCompare(b.name!),
    },
    {
      title: "Default",
      dataIndex: "is_default",
      key: "is_default",
      render: text => (text ? "Yes" : "No"),
    },
    {
      title: "Schema Name",
      dataIndex: "schema_name",
      key: "schema_name",
      render: (_, record) => {
        const value = record.schema_name;
        if (!value) {
          return "Not selected";
        }
        const schema = getSchemaMasterById(value);
        return schema?.label ?? "Not selected";
      },
    },
    {
      title: "Is Active",
      width: 120,
      render: (record: any) => {
        return (
          <ActiveToggleButton
            table={tableName.print_format}
            pk={record.id}
            is_active={record.is_active}
          />
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() =>
            navigate(navigationRoutes.PRINT_FORMAT_EDIT(record.id))
          }
          type="text"
          title="Edit Print Format"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this print format?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete Print Format"
          />
        </Popconfirm>,
      ],
    },
  ];

  return (
    <BaseTable<PrintFormat>
      loading={isLoading}
      columns={columns}
      dataSource={gridData}
      page={page}
      pageSize={pageSize}
      total={total}
      rowKey="id"
      toolBarRender={() => [
        <CustomButton
          key="add"
          type="primary"
          icon={<Plus size={16} />}
          onClick={() => navigate(navigationRoutes.PRINT_FORMAT_ADD)}
          title="Add New Print Format"
        >
          Add Print Format
        </CustomButton>,
      ]}
      headerTitle="Print Format List"
      onPageChange={(page, size) => {
        setPage(page);
        setPageSize(size ?? 10);
        searchParams.set("page", page.toString());
        searchParams.set("pageSize", size.toString());
        navigate(`${navigationRoutes.PRINT_FORMAT}?${searchParams.toString()}`);
      }}
      request={() => refetch()}
      tableName={gridName.printFormatTable}
    />
  );
};

export default PrintFormatListPage;
